import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  readCookieConsent,
  writeCookieConsent,
  enableAnalytics,
  disableAnalytics,
  trackPageView,
} from '../../../util/analytics';

const Banner = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: min(420px, 92vw);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 1.6rem;
  padding: 1.6rem 1.7rem;
  color: var(--ink);
  box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
  z-index: 1000;
  backdrop-filter: blur(14px);

  @media (max-width: 768px) {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
    width: auto;
  }
`;

const Title = styled.h3`
  margin: 0 0 0.6rem;
  font-size: 1.6rem;
  font-weight: 500;
  color: var(--ink);
`;

const Body = styled.p`
  margin: 0 0 1.2rem;
  font-size: 1.4rem;
  line-height: 1.6;
  color: var(--muted);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
`;

const PrimaryButton = styled.button`
  min-height: 44px;
  padding: 0.7rem 1.2rem;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
  }
`;

const SecondaryButton = styled.button`
  min-height: 44px;
  padding: 0.7rem 1.1rem;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: var(--ink);
  }
`;

const Preferences = styled.div`
  border-top: 1px solid var(--line);
  padding-top: 1rem;
  margin-top: 1rem;
  display: grid;
  gap: 0.8rem;
`;

const PreferenceRow = styled.label`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  font-size: 1.4rem;
  color: var(--muted);
`;

const PreferenceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  span {
    font-size: 1.4rem;
    color: var(--muted);
  }
`;

const Toggle = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  width: 48px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface-strong);
  position: relative;
  cursor: pointer;
  padding: 0;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $active }) => ($active ? '24px' : '4px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--ink);
    transition: left 0.2s ease;
  }
`;

const FooterLinks = styled.div`
  margin-top: 0.9rem;
  font-size: 1.4rem;
  color: var(--muted);

  a {
    color: var(--muted);
    text-decoration: none;
  }
`;

const CookieConsent = () => {
  const { i18n } = useTranslation('common');
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const legalPath =
    lang === 'fr'
      ? '/fr/mentions-legales-et-cookies'
      : '/en/legal-notice-cookies';

  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  const copy = useMemo(
    () =>
      lang === 'fr'
        ? {
            title: 'Cookies, simplement',
            body: 'Nous utilisons des cookies essentiels pour le bon fonctionnement du site et des cookies de mesure d’audience pour comprendre l’usage. À vous de choisir.',
            accept: 'Tout accepter',
            reject: 'Tout refuser',
            preferences: 'Préférences',
            save: 'Enregistrer',
            essentialTitle: 'Essentiels',
            essentialDesc: 'Toujours actifs pour la sécurité et la stabilité.',
            analyticsTitle: 'Mesure d’audience',
            analyticsDesc: 'Google Analytics pour améliorer le site.',
            learn: 'En savoir plus',
          }
        : {
            title: 'Cookies, simply',
            body: 'We use essential cookies to keep the site working and analytics cookies to understand usage. You choose what to allow.',
            accept: 'Accept all',
            reject: 'Reject',
            preferences: 'Preferences',
            save: 'Save choices',
            essentialTitle: 'Essential',
            essentialDesc: 'Always on for security and stability.',
            analyticsTitle: 'Analytics',
            analyticsDesc: 'Google Analytics to improve the site.',
            learn: 'Learn more',
          },
    [lang],
  );

  useEffect(() => {
    const consent = readCookieConsent();
    if (!consent) {
      setVisible(true);
      return;
    }

    setAnalyticsEnabled(consent.analytics);
  }, []);

  const handleAccept = () => {
    const consent = {
      version: 1,
      updatedAt: new Date().toISOString(),
      analytics: true,
    };
    writeCookieConsent(consent);
    setAnalyticsEnabled(true);
    enableAnalytics();
    trackPageView(window.location.pathname + window.location.search);
    setVisible(false);
  };

  const handleReject = () => {
    const consent = {
      version: 1,
      updatedAt: new Date().toISOString(),
      analytics: false,
    };
    writeCookieConsent(consent);
    setAnalyticsEnabled(false);
    disableAnalytics();
    setVisible(false);
  };

  const handleSave = () => {
    const consent = {
      version: 1,
      updatedAt: new Date().toISOString(),
      analytics: analyticsEnabled,
    };
    writeCookieConsent(consent);
    if (analyticsEnabled) {
      enableAnalytics();
      trackPageView(window.location.pathname + window.location.search);
    } else {
      disableAnalytics();
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Banner role="dialog" aria-live="polite" aria-label="Cookie consent">
      <Title>{copy.title}</Title>
      <Body>{copy.body}</Body>
      <Actions>
        <PrimaryButton onClick={handleAccept}>{copy.accept}</PrimaryButton>
        <SecondaryButton onClick={handleReject}>{copy.reject}</SecondaryButton>
        <SecondaryButton onClick={() => setShowPrefs((prev) => !prev)}>
          {copy.preferences}
        </SecondaryButton>
      </Actions>

      {showPrefs && (
        <Preferences>
          <PreferenceRow>
            <PreferenceInfo>
              {copy.essentialTitle}
              <span>{copy.essentialDesc}</span>
            </PreferenceInfo>
            <Toggle
              $active={true}
              aria-pressed
              disabled
              aria-label="Essential cookies always active"
            >
              <span style={{ display: 'none' }}>Essential</span>
            </Toggle>
          </PreferenceRow>
          <PreferenceRow>
            <PreferenceInfo>
              {copy.analyticsTitle}
              <span>{copy.analyticsDesc}</span>
            </PreferenceInfo>
            <Toggle
              type="button"
              aria-label={copy.analyticsTitle}
              $active={analyticsEnabled}
              aria-pressed={analyticsEnabled}
              onClick={() => setAnalyticsEnabled((prev) => !prev)}
            />
          </PreferenceRow>
          <Actions>
            <PrimaryButton onClick={handleSave}>{copy.save}</PrimaryButton>
          </Actions>
        </Preferences>
      )}

      <FooterLinks>
        <a href={legalPath}>{copy.learn}</a>
      </FooterLinks>
    </Banner>
  );
};

export default CookieConsent;
